import moment from "moment";
import { callApi } from "@/lib/apiClient/api";
import { getIn } from "formik";
import { isEmpty } from "@/lib/filters/funcs";

const NO_EVENT_FIELD_TYPES = [
  "autocompleteInput",
  "custom",
  "date",
  "time",
  "datetime",
  "file",
  "timezone",
  "promoCode",
  "checkbox"
];

const formatDate = (val) => (val ? moment(val).format("YYYY-MM-DD") : null);

const setValue =
  (type, fieldName, setFieldValue, trim = false, form, setStatus) =>
  (val) => {
    let value = NO_EVENT_FIELD_TYPES.includes(type) ? val : val.target.value;
    if (trim && type !== "password") {
      value = value.trim();
    }
    // We need to keep API errors visible so set them in form status
    if (form && setStatus && !isEmpty(form.status?.apiErrors)) {
      setStatus({
        apiErrors: { ...form.status.apiErrors, [fieldName]: false }
      });
    }
    setFieldValue(fieldName, type === "date" ? formatDate(value) : value);
  };

const getResetFieldsOnChangeFunc =
  (type, fieldName, setFieldValue, resetFieldsOnChange, values) => (val) => {
    setValue(type, fieldName, setFieldValue)(val);
    const fieldsToReset = resetFieldsOnChange({
      formValues: values,
      chosenValue: val.target ? val.target.value : val
    });
    if (fieldsToReset) {
      fieldsToReset.forEach((curFieldName) =>
        setFieldValue(curFieldName, null)
      );
    }
  };
const getSetFieldsOnChangeFunc =
  (
    type,
    fieldName,
    setFieldValue,
    setFieldsOnChange,
    values,
    setFieldTouched
  ) =>
  (val) => {
    setValue(type, fieldName, setFieldValue)(val);
    const fieldsToSet = setFieldsOnChange({
      formValues: values,
      chosenValue: val.target ? val.target.value : val
    });

    if (fieldsToSet) {
      Object.keys(fieldsToSet).forEach((field) => {
        setFieldTouched(field, true);
        setFieldValue(field, fieldsToSet[field]);
      });
    }
  };
const handleOnChange = (
  resetFieldsOnChange,
  setFieldsOnChange,
  type,
  fieldName,
  setFieldValue,
  values,
  setFieldTouched,
  form,
  setStatus
) => {
  if (resetFieldsOnChange) {
    return getResetFieldsOnChangeFunc(
      type,
      fieldName,
      setFieldValue,
      resetFieldsOnChange,
      values
    );
  }

  return setFieldsOnChange
    ? getSetFieldsOnChangeFunc(
        type,
        fieldName,
        setFieldValue,
        setFieldsOnChange,
        values,
        setFieldTouched
      )
    : setValue(type, fieldName, setFieldValue, false, form, setStatus);
};

const handleBlur = (e, onBlur, type, setFieldValue, fieldName) => {
  onBlur(e);
  setValue(type, fieldName, setFieldValue, true)(e);
};

export const getFieldProps = ({
  field: {
    name,
    placeholder,
    type,
    apiParams,
    searchParamName,
    isDisabled,
    isReadOnly,
    options,
    setDynamicOptions,
    resource,
    canBeDeleted,
    setDynamicProps,
    isNotAvailable,
    ...rest
  },
  fieldName,
  value,
  values,
  setFieldTouched,
  setFieldValue,
  setStatus,
  onBlur,
  meta,
  form,
  mode,
  resetFieldsOnChange,
  setFieldsOnChange,
  fieldRef,
  handleUploadsInProgress
}) => ({
  input: {
    name: fieldName,
    value: value ?? "",
    onChange: handleOnChange(
      resetFieldsOnChange,
      setFieldsOnChange,
      type,
      fieldName,
      setFieldValue,
      values,
      setFieldTouched,
      form,
      setStatus
    ),
    onBlur: ["autocompleteInput", "datetime"].includes(type)
      ? () => setFieldTouched(fieldName, true)
      : (e) => handleBlur(e, onBlur, type, setFieldValue, fieldName)
  },
  type,
  options: setDynamicOptions
    ? setDynamicOptions({
        mode,
        formValues: { ...values, canBeDeleted }
      })
    : options,
  meta: {
    // update meta so error can come and form status where we keep API errors
    ...meta,
    error: meta.error || getIn(form.status, ["apiErrors", name])
  },
  disabled: isDisabled
    ? isDisabled({
        mode,
        formValues: { ...values, canBeDeleted }
      })
    : null,
  readOnly: isReadOnly
    ? isReadOnly({
        mode,
        formValues: { ...values, canBeDeleted }
      })
    : null,
  dynamicProps: setDynamicProps
    ? setDynamicProps({ formValues: { ...values } })
    : null,
  placeholder:
    typeof placeholder === "function"
      ? placeholder({ mode, formValues: { ...values } })
      : placeholder,
  fieldApiParams: resource
    ? {
        apiParams:
          typeof apiParams === "function" ? apiParams(values) : apiParams,
        resource,
        searchParamName
      }
    : null,
  setFieldTouched,
  mode,
  fieldRef,
  setFieldValue,
  isNotAvailable: isNotAvailable ? isNotAvailable(values) : null,
  setStatus,
  values,
  handleUploadsInProgress,
  ...rest
});

export const getRequiredValues = ({ fields }) =>
  fields
    .filter(({ isRequired }) => isRequired)
    .reduce((acc, { name }) => {
      acc[name] = null;
      return acc;
    }, {});

// TO-DO Unused for now in future can be deleted
export const formatInitialValues = (values) =>
  Object.entries(values).reduce((acc, keyValuePair) => {
    if (keyValuePair[1] === 0) {
      acc[keyValuePair[0]] = "0";
    } else {
      acc[keyValuePair[0]] = keyValuePair[1];
    }
    return acc;
  }, {});

export const getVisibleFields = ({ fields, values, mode }) =>
  fields.filter(
    (field) =>
      field.type !== "hidden" &&
      (!field.isVisible || field.isVisible(values, mode))
  );

export const getCleanedValues = (submittedValues, fields, mode) => {
  fields = fields.filter((field) => {
    return (
      field.type !== "hidden" &&
      field.isVisible &&
      !field.isVisible(submittedValues, mode)
    );
  });

  fields.map(
    (field) => submittedValues[field.name] && delete submittedValues[field.name]
  );

  return submittedValues;
};

export const logFormError = (error, origin) => {
  const type =
    origin === "preSubmit"
      ? "Form pre-submit error"
      : "API error returned after form submit";
  const { status, request, response, message } = { ...error };
  callApi({
    url: "/logging/frontendApps",
    method: "POST",
    params: {
      level: "error",
      message: `
      type: ${type},
      message: ${origin === "api" ? message : error}, 
      status: ${status}, 
      request: ${JSON.stringify(request)}, 
      response: ${JSON.stringify(response)}, 
      location: ${window.location.toString()}`,
      platform: "js"
    }
  });
};

const handleVisibleFieldErrors = (formActions, errors) => {
  formActions.setTouched(errors, false);
  formActions.setErrors(errors);
  // set api errors in form status to keep them when bluring fields
  formActions.setStatus({ apiErrors: errors });
};

export const renderErrorList = (errors) => {
  const errorMessages = Object.values(errors);
  const isSingleError = errorMessages.length === 1;
  return (
    <div>
      {errorMessages.map((error) => (
        <p key={error}>
          {!isSingleError && <span className="me-2">&bull;</span>}
          {error}
        </p>
      ))}
    </div>
  );
};

const handle422Errors = (formActions, errors, error) => {
  // first check if parsed Errors which are not tied to a visible field can be shown
  if (!isEmpty(errors)) {
    formActions.setStatus({ error: renderErrorList(errors) });
    return;
  }
  // no parsed errors in response so just show the description
  formActions.setStatus({
    error: error?.response?.data?.["hydra:description"]
  });
};

const defaultErrorMessage =
  "An unexpected error has occurred. If the error persists please contact support.";

export const createApiErrorHandler =
  (formActions, visibleFields) =>
  ({
    errors: apiFieldErrors = {},
    generalError = "",
    error,
    origin = "api"
  } = {}) => {
    formActions.setSubmitting(false);
    const visibleFieldsNames = new Set(visibleFields.map((f) => f.name));
    const hasErrorsOnVisibleFields = Object.keys(apiFieldErrors).some(
      (errorKey) => visibleFieldsNames.has(errorKey)
    );
    // first show errors tied to visible fields
    if (hasErrorsOnVisibleFields) {
      handleVisibleFieldErrors(formActions, apiFieldErrors);
      return;
    }
    // then check if error code is 422 and handle the errors in it
    if (error?.response?.status === 422) {
      // we are free show specific errors
      handle422Errors(formActions, apiFieldErrors, error);
      return;
    }
    // finally show general error message and log the error in production
    formActions.setStatus({
      error: generalError || defaultErrorMessage
    });
    if (process.env.NODE_ENV === "production") {
      logFormError(error, origin);
    }
  };
